'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import { geocodeSearchFull, type GeocodeQuality } from '@/lib/geocoding/search';

interface GooglePlacesAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

interface PhotonFeature {
  properties: {
    name?: string;
    street?: string;
    housenumber?: string;
    postcode?: string;
    city?: string;
    state?: string;
    country?: string;
  };
  geometry: {
    coordinates: [number, number];
  };
}

/**
 * Address autocomplete input using Photon API (free, no API key).
 * Drop-in replacement for the former Google Places Autocomplete component.
 */
export default function GooglePlacesAutocomplete({
  value,
  onChange,
  placeholder = 'Start typing to search for an address...',
  className = '',
  disabled = false
}: GooglePlacesAutocompleteProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [localValue, setLocalValue] = useState(value);
  const [results, setResults] = useState<PhotonFeature[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isIntensifying, setIsIntensifying] = useState(false);
  const [quality, setQuality] = useState<GeocodeQuality>('low');
  const [resultSource, setResultSource] = useState<'free' | 'google'>('free');
  const isSelectingRef = useRef(false);
  const lastQueryRef = useRef('');

  useEffect(() => {
    if (!isSelectingRef.current) {
      setLocalValue(value);
    }
  }, [value]);

  const performSearch = useCallback(async (query: string, intensify = false) => {
    if (query.trim().length < 3) {
      setResults([]);
      setQuality('low');
      return;
    }
    if (intensify) {
      setIsIntensifying(true);
    } else {
      setIsLoading(true);
    }
    try {
      const { features, quality: q, source } = await geocodeSearchFull(query, 5, undefined, intensify);
      setResults(features as PhotonFeature[]);
      setQuality(q);
      setResultSource(source);
      setShowDropdown(true);
    } catch {
      setResults([]);
      setQuality('low');
    } finally {
      if (intensify) {
        setIsIntensifying(false);
      } else {
        setIsLoading(false);
      }
    }
  }, []);

  const handleIntensify = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (lastQueryRef.current.trim().length >= 3) {
      performSearch(lastQueryRef.current, true);
    }
  }, [performSearch]);

  const formatAddress = (props: PhotonFeature['properties']): string => {
    const parts: string[] = [];
    if (props.street) parts.push(props.housenumber ? `${props.housenumber} ${props.street}` : props.street);
    if (props.postcode) parts.push(props.postcode);
    if (props.city) parts.push(props.city);
    if (props.state) parts.push(props.state);
    if (props.country) parts.push(props.country);
    return parts.join(', ');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    onChange(newValue);
    lastQueryRef.current = newValue;
    setResultSource('free');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      performSearch(newValue);
    }, 300);
  };

  const handleSelect = (feature: PhotonFeature) => {
    const address = formatAddress(feature.properties);
    isSelectingRef.current = true;
    setLocalValue(address);
    onChange(address);
    setResults([]);
    setShowDropdown(false);
    setTimeout(() => {
      isSelectingRef.current = false;
    }, 100);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && showDropdown && results.length > 0) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <div className={`relative ${className}`} style={{ position: 'relative', zIndex: 1 }}>
      <input
        ref={inputRef}
        type="text"
        value={localValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => { if (results.length > 0) setShowDropdown(true); }}
        onBlur={() => { setTimeout(() => setShowDropdown(false), 200); }}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                   bg-white dark:bg-gray-800
                   text-gray-900 dark:text-gray-100
                   placeholder-gray-400 dark:placeholder-gray-500
                   focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                   disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:cursor-not-allowed"
      />
      {isLoading && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
          <svg className="w-4 h-4 text-gray-400 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" opacity="0.3" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3a9 9 0 019 9" />
          </svg>
        </div>
      )}

      {showDropdown && (results.length > 0 || isIntensifying) && (
        <div className="absolute z-[99999] mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 shadow-lg max-h-60 overflow-y-auto bg-white dark:bg-gray-800">
          {results.map((feature, idx) => {
            const name = feature.properties.name || feature.properties.street || 'Unknown';
            const address = formatAddress(feature.properties);
            return (
              <button
                key={idx}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); handleSelect(feature); }}
                className="w-full text-left px-3 py-2 text-sm border-b border-gray-100 last:border-b-0 hover:bg-gray-100"
              >
                <div className="font-medium text-gray-900">{name}</div>
                {address && (
                  <div className="text-xs text-gray-500">{address}</div>
                )}
              </button>
            );
          })}
          {isIntensifying && (
            <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" />
              Searching deeper...
            </div>
          )}
          {resultSource === 'free' && !isIntensifying && (
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); handleIntensify(); }}
              className={`w-full text-left px-3 py-2 text-sm border-t flex items-center gap-2 transition-colors ${
                quality === 'low'
                  ? 'bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 text-blue-600 dark:text-blue-300 border-gray-200 dark:border-gray-700'
                  : 'bg-gray-50 hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700'
              }`}
            >
              <Sparkles className={`w-3.5 h-3.5 ${quality === 'low' ? 'animate-pulse' : ''}`} />
              <span>Search deeper{quality === 'low' ? ' — better results likely' : ''}</span>
            </button>
          )}
        </div>
      )}
      {showDropdown && results.length === 0 && !isIntensifying && !isLoading && quality === 'low' && lastQueryRef.current.trim().length >= 3 && (
        <div className="absolute z-[99999] mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 shadow-lg bg-white dark:bg-gray-800">
          <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">No results found.</div>
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); handleIntensify(); }}
            className="w-full text-left px-3 py-2 text-sm border-t bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 text-blue-600 dark:text-blue-300 border-gray-200 dark:border-gray-700 flex items-center gap-2 transition-colors"
          >
            <Sparkles className="w-3.5 h-3.5 animate-pulse" />
            <span>Search deeper</span>
          </button>
        </div>
      )}
    </div>
  );
}
